/**
 * Unified Dashboard Header Component
 * Consistent header layout for both Admin and User dashboards
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Logo from './Logo';
import './UnifiedDashboardHeader.css';

const UnifiedDashboardHeader = ({
    isAdmin = false,
    onLogout,
    userRole
}) => {
    const { user } = useAuth();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const formatUserEmail = (email) => {
        // Handle null, undefined, or empty email
        if (!email || typeof email !== 'string') {
            return isAdmin ? 'Admin' : 'User';
        }

        try {
            // Trim whitespace and convert to lowercase for consistency
            const cleanEmail = email.trim().toLowerCase();

            // Basic email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(cleanEmail)) {
                console.warn('Invalid email format:', email);
                return isAdmin ? 'Admin' : 'User';
            }

            // Split email and handle potential issues
            const emailParts = cleanEmail.split('@');

            // Ensure we have exactly 2 parts (username and domain)
            if (emailParts.length !== 2) {
                console.warn('Email does not contain exactly one @ symbol:', email);
                return isAdmin ? 'Admin' : 'User';
            }

            const [username, domain] = emailParts;

            // Validate username and domain are not empty
            if (!username || !domain) {
                console.warn('Email has empty username or domain:', email);
                return isAdmin ? 'Admin' : 'User';
            }

            // Handle very long usernames
            if (username.length > 15) {
                return `${username.substring(0, 15)}...`;
            }

            return username;
        } catch (error) {
            console.error('Error formatting user email:', error, 'Original email:', email);
            return isAdmin ? 'Admin' : 'User';
        }
    };

    // Handle click outside to close user menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleUserMenuToggle = () => {
        setIsUserMenuOpen(!isUserMenuOpen);
    };

    const handleLogout = () => {
        setIsUserMenuOpen(false);
        onLogout();
    };

    const getDashboardInfo = () => {
        if (isAdmin) {
            return {
                icon: <Logo width="32" height="32" alt="AFF Logo" />,
                title: 'Admin Portal',
                subtitle: 'Film Society Management System'
            };
        } else {
            return {
                icon: '🎬',
                title: 'AFF Portal',
                subtitle: 'Australian Film Society Federation'
            };
        }
    };

    const dashboardInfo = getDashboardInfo();

    return (
        <div className="unified-dashboard-header">
            <div className="unified-dashboard-header-content">
                <div className="unified-dashboard-title-section">
                    <div className="unified-dashboard-icon">
                        <span className="icon">{dashboardInfo.icon}</span>
                    </div>
                    <div className="unified-dashboard-title-info">
                        <h2>{dashboardInfo.title}</h2>
                        <p>{dashboardInfo.subtitle}</p>
                    </div>
                </div>

                <div className="unified-dashboard-actions-section">
                    <div className="unified-user-menu-container" ref={userMenuRef}>
                        <button
                            className={`unified-user-profile ${isUserMenuOpen ? 'active' : ''}`}
                            onClick={handleUserMenuToggle}
                            title="User Menu"
                        >
                            <div className="unified-user-avatar">
                                <span className="avatar-text">
                                    {user?.first_name ? user.first_name.charAt(0).toUpperCase() :
                                        user?.email ? user.email.charAt(0).toUpperCase() :
                                            isAdmin ? 'A' : 'U'}
                                </span>
                            </div>
                            <div className="unified-user-details">
                                <span className="unified-user-name">
                                    {user?.first_name && user?.last_name
                                        ? `${user.first_name} ${user.last_name}`
                                        : formatUserEmail(user?.email) || (isAdmin ? 'Admin User' : 'User')
                                    }
                                </span>
                                <span className="unified-user-email">{user?.email}</span>
                            </div>
                            <span className={`unified-dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`}>▼</span>
                        </button>

                        {/* Dropdown Menu */}
                        {isUserMenuOpen && (
                            <div className="unified-user-dropdown">
                                <div className="unified-dropdown-menu">
                                    <button onClick={handleLogout} className="btn btn-primary btn-sm">
                                        <span>🚪</span>
                                        <span>Sign Out</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnifiedDashboardHeader;
